import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { capitalize } from "lodash-es";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { PlaceTypeIcon } from "@/components/ui/status/place-type-icon";
import { PlaceType } from "@/types/openapi-enum";

const PLACE_TYPE_OPTIONS: ComboboxOption[] = [
  PlaceType.House,
  PlaceType.Condo,
  PlaceType.Building,
].map((type) => ({
  value: type,
  label: capitalize(type),
  detail: (
    <span className="grow flex items-center gap-2">
      <PlaceTypeIcon type={type} className="size-5" />
      {capitalize(type)}
    </span>
  ),
  trigger: (
    <span className="flex grow items-center gap-2 text-sm line-clamp-1">
      <PlaceTypeIcon type={type} className="size-5" />
      {capitalize(type)}
    </span>
  ),
}));

export type PlaceTypeSelectProps = FieldWrapperInjectedProps & {
  /** `""` = nothing selected. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  modal?: boolean;
  "data-test"?: string;
};

export default function PlaceTypeSelect({
  value,
  onChange,
  placeholder = "Select type",
  ...rest
}: PlaceTypeSelectProps) {
  return (
    <Combobox
      data-test="place-type-select-trigger"
      {...rest}
      value={value || null}
      onChange={(next) => onChange(next ?? "")}
      options={PLACE_TYPE_OPTIONS}
      placeholder={placeholder}
      searchPlaceholder="Search type..."
      emptyText="No type found."
      renderTrigger={(selected) =>
        selected?.trigger ?? (
          <p className="text-sm text-text-dark-min line-clamp-1 grow">
            {placeholder}
          </p>
        )
      }
    />
  );
}
